import { PROTECTED_EMPLOYEE_ADR_ORG } from './constants';
import type { ProtectedEmployeeProfile } from './types';

type CallQueueEntry = {
  additionalData?: Record<string, unknown> | null;
};

type EmergencyCallsState = {
  selectedCallerID?: string | null;
  callQueue?: Record<string, CallQueueEntry | undefined>;
};

type RootState = {
  emergencyCalls?: EmergencyCallsState;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const asOptionalString = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

/**
 * Normalize a raw ADR entry into a PE profile. Returns null when the payload
 * is not a usable object (presence of the org entry is the consent gate from BE).
 */
export const normalizeProtectedEmployeeProfile = (
  raw: unknown,
): ProtectedEmployeeProfile | null => {
  if (!isRecord(raw)) {
    return null;
  }

  return {
    organization: asOptionalString(raw.organization),
    safetyContactName: asOptionalString(raw.safety_contact_name),
    safetyContactPhone: asOptionalString(raw.safety_contact_phone),
  };
};

/**
 * Read the first Protected Employee ADR entry for the selected (or given) call.
 */
export const selectProtectedEmployeeForCall = (
  state: RootState,
  callerID?: string | null,
): ProtectedEmployeeProfile | null => {
  const emergencyCalls = state.emergencyCalls;
  if (!emergencyCalls) {
    return null;
  }

  const resolvedCallerID = callerID ?? emergencyCalls.selectedCallerID;
  if (!resolvedCallerID) {
    return null;
  }

  const additionalData =
    emergencyCalls.callQueue?.[resolvedCallerID]?.additionalData;
  if (!isRecord(additionalData)) {
    return null;
  }

  const orgEntries = additionalData[PROTECTED_EMPLOYEE_ADR_ORG];
  if (!Array.isArray(orgEntries) || orgEntries.length === 0) {
    return null;
  }

  // Hermes stores ADR under entry.data; flat fixtures / tests may omit the wrap.
  const entry = orgEntries[0];
  const profilePayload =
    isRecord(entry) && isRecord(entry.data) ? entry.data : entry;
  return normalizeProtectedEmployeeProfile(profilePayload);
};

/**
 * Single display line for the GSOC safety contact, e.g.
 * "Acme Corp Security · 1 720 555 0142". Null when the org provided neither
 * a contact name nor phone.
 */
export const getProtectedEmployeeSafetyContactLine = (
  profile: ProtectedEmployeeProfile | null | undefined,
): string | null => {
  const parts = [profile?.safetyContactName, profile?.safetyContactPhone]
    .map(part => part?.trim())
    .filter((part): part is string => !!part);
  return parts.length > 0 ? parts.join(' · ') : null;
};
